/**
 * Frontend RBAC — the single source of truth for which roles may access each
 * route. Used by both RoleRoute (route guard) and AppShell (nav filtering) so
 * the menu and the routing stay in lock-step.
 *
 * Role codes ↔ scope user_roles:
 *   admin       → Platform Administrator
 *   user        → Messaging User
 *   compliance  → Compliance/Audit Officer
 *   integration → Integration/System Administrator
 *
 * A path mapped to `undefined` (or absent) is open to ANY authenticated user.
 */
export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  COMPLIANCE: 'compliance',
  INTEGRATION: 'integration',
};

export const ROUTE_ROLES = {
  // Open to any authenticated user
  '/dashboard': undefined,
  '/my-profile': undefined,
  '/my-credit-balance': undefined,

  // Messaging User features (admin may oversee)
  '/contact-management': ['user', 'admin'],
  '/sender-id-request-queue': ['user', 'admin'],
  '/compose-sms': ['user', 'admin'],
  '/group-sms': ['user', 'admin'],
  '/bulk-messaging-upload': ['user', 'admin'],
  '/message-templates': ['user', 'admin'],
  '/email-compose': ['user', 'admin'],
  '/email-templates': ['user', 'admin'],
  '/push-notifications': ['user', 'admin'],

  // Platform Administrator only
  '/user-administration': ['admin'],
  '/credit-allocation': ['admin'],
  '/sla-support-incidents': ['admin'],
  '/project-training-schedules': ['admin'],

  // Oversight views — admin + compliance (read-only)
  '/messaging-analytics-dashboard': ['admin', 'compliance'],
  '/statistical-reports': ['admin', 'compliance'],
  '/portal-content': ['admin'],
  '/data-export': ['admin'],

  // Integration/System Administrator (+ admin)
  '/provider-settings': ['admin', 'integration'],
  '/api-configuration': ['admin', 'integration'],
  '/webhook-configuration': ['admin', 'integration'],

  // Compliance/Audit Officer (+ admin)
  '/audit-log-history': ['admin', 'compliance'],
  '/audit-activity-logs': ['admin', 'compliance'],
};

/** True when `role` may access `path` (open routes allow everyone). */
export function canAccess(role, path) {
  const allowed = ROUTE_ROLES[path];
  if (!allowed) return true;
  return allowed.includes(role);
}

export default { ROLES, ROUTE_ROLES, canAccess };
